import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { PropertyStatus, type PropertyStatusConfiguration } from '@/features/domaine/property-status'
import type { ContextReglesCxpi } from '@/features/domaine/regles-akto/CXPI/types.ts'
import { isNotNullUndefinedOrEmptyString, isNullUndefinedOrEmptyString } from '@/features/shared/tools'

function obligatoireSurCIPI(_value: any, _parent: any, model: ContratCxpiDTO) {
  return model.sousDispositif === 'CIPI' ? PropertyStatus.Required : PropertyStatus.NotPresent
}

function obligatoireSurCDPI(_value: any, _parent: any, model: ContratCxpiDTO) {
  return model.sousDispositif === 'CDPI' ? PropertyStatus.Required : PropertyStatus.NotPresent
}

export const propertyStatusConfigurationDossier: PropertyStatusConfiguration<ContratCxpiDTO, ContextReglesCxpi> = {
  employeur: {
    contact: {
      email: PropertyStatus.Required,
      telephone: PropertyStatus.Optional,
    },
    effectifTotalEntreprise: PropertyStatus.Required,
  },
  salarie: {
    nomNaissance: PropertyStatus.Required,
    nom: PropertyStatus.Optional,
    prenom: PropertyStatus.Required,
    sexe: PropertyStatus.Required,
    dateNaissance: PropertyStatus.Required,
    nirTemporaire: PropertyStatus.Required,
    nir: PropertyStatus.Required,
    beneficieReconnaissanceTravailleurHandicape: PropertyStatus.Required,
    adresse: {
      voie: PropertyStatus.Required,
      complement: PropertyStatus.Optional,
      codePostal: PropertyStatus.Required,
      commune: PropertyStatus.Required,
    },
    email: PropertyStatus.Required,
    csp: PropertyStatus.Required,
    contratPacea: (_value, parent) =>
      parent.contratEngagementJeune === true ? PropertyStatus.NotPresent : PropertyStatus.Required,
    contratEngagementJeune: (_value, parent) =>
      parent.contratPacea === true ? PropertyStatus.NotPresent : PropertyStatus.Required,
    inscritFranceTravail: PropertyStatus.Required,
    justifieAncienneteInterim: obligatoireSurCDPI,
    nombreHeuresAncienneteInterim: (_value, parent, model) =>
      model.sousDispositif === 'CDPI' && parent.justifieAncienneteInterim === true
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    categoriePublic: (_value, parent, model) =>
      model.sousDispositif === 'CIPI' || parent.justifieAncienneteInterim === false
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    niveauEntreeFormation: PropertyStatus.Required,
    diplomeOuTitreLePlusEleveObtenu: (_value, parent, _model) => {
      if (parent.categoriePublic === 'QualifInferieurCapBep') {
        return PropertyStatus.Required
      }
      return PropertyStatus.Optional
    },
    intituleDiplome: PropertyStatus.Optional,

    tuteur: {
      nom: (_value, parent, _model, context) => {
        if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

        if (
          isNotNullUndefinedOrEmptyString(parent.nom) ||
          isNotNullUndefinedOrEmptyString(parent.prenom) ||
          isNotNullUndefinedOrEmptyString(parent.emploiOccupe)
        )
          return PropertyStatus.Required

        return PropertyStatus.Optional
      },
      prenom: (_value, parent, _model, context) => {
        if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.Optional

        if (
          isNotNullUndefinedOrEmptyString(parent.nom) ||
          isNotNullUndefinedOrEmptyString(parent.prenom) ||
          isNotNullUndefinedOrEmptyString(parent.emploiOccupe)
        )
          return PropertyStatus.Required

        return PropertyStatus.Optional
      },
      emploiOccupe: PropertyStatus.Optional,
    },
  },
  contrat: {
    referenceProjet: PropertyStatus.Optional,
    referenceInterneEntreprise: PropertyStatus.Optional,
    nombreContrats: PropertyStatus.Required,
    codeDrf: PropertyStatus.Required,
    cdiInterimaire: PropertyStatus.Required,
    dateConclusion: (_value, parent) =>
      parent.cdiInterimaire === true ? PropertyStatus.Required : PropertyStatus.NotPresent,
    tempsPlein: (_value, parent) =>
      parent.cdiInterimaire === true ? PropertyStatus.Required : PropertyStatus.NotPresent,
    dureeHebdomadaire: {
      $model: (_, parent) => (parent.cdiInterimaire === true ? PropertyStatus.Required : PropertyStatus.NotPresent),
    },
    dateDebutContrat: obligatoireSurCIPI,
    dateFinContrat: obligatoireSurCIPI,
    salaireHoraireFormation: PropertyStatus.Required,
    salaireHoraireMission: obligatoireSurCIPI,
    periodeInterruption: PropertyStatus.Required,
    interruption: {
      dateDebutInterruption: PropertyStatus.Required,
      dateFinInterruption: PropertyStatus.Required,
    },
    parcoursPTP: (_value, _parent, model) =>
      model.sousDispositif === 'CDPI' ? PropertyStatus.Required : PropertyStatus.NotPresent,
  },
  formation: {
    diplomeOuTitreVise: PropertyStatus.Required,
    // typeQualification: PropertyStatus.Required,
    // conventionCollective: PropertyStatus.Required,
    // niveau: PropertyStatus.Required,
    // coefficient: PropertyStatus.Required,
    // nombreCCP: PropertyStatus.Required,
    // typeReferentielFormation: PropertyStatus.Required,
    /*
    rncp: {
      $model: (_, parent) =>
        parent.typeReferentielFormation === 'Rncp' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    },
    formacode: {
      $model: (_, parent) =>
        parent.typeReferentielFormation === 'Formacode' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    },
    nsf: {
      $model: PropertyStatus.Required,
    },
    intitule: PropertyStatus.Required,
    */
    objectifGeneralFormation: PropertyStatus.Required,
    dureeTotale: PropertyStatus.Required,
    dureeTotaleBilans: PropertyStatus.Required,
    dureeMinimumMission: obligatoireSurCDPI,
    modules: {
      formationRealiseeDansEntreprise: PropertyStatus.Required,
      etablissementPrestataire: {
        $model: PropertyStatus.Required,
        siret: {
          $model: PropertyStatus.Required,
        },
      },
      modalitePedagogique: PropertyStatus.Required,
      lieuFormationIdentiqueCfaPrincipal: (...[, , parent]) => {
        if (!parent.modalitePedagogique) return PropertyStatus.NotPresent
        return ['Afest', 'Distance'].includes(parent.modalitePedagogique)
          ? PropertyStatus.NotPresent
          : PropertyStatus.Required
      },
      lieuFormation: {
        $model: (...[, parent]) => {
          if (!parent.modalitePedagogique) return PropertyStatus.NotPresent
          if (['Afest', 'Distance'].includes(parent.modalitePedagogique)) return PropertyStatus.NotPresent
          return parent.lieuFormationIdentiqueCfaPrincipal === true
            ? PropertyStatus.NotPresent
            : PropertyStatus.Required
        },
        voie: PropertyStatus.Required,
        commune: PropertyStatus.Required,
        codePostal: PropertyStatus.Required,
      },
      dureeFoad: (...[, , parent]) =>
        parent.modalitePedagogique === 'Mixte' ? PropertyStatus.Required : PropertyStatus.NotPresent,
      duree: PropertyStatus.Required,
      horairesFormation: PropertyStatus.Optional,
      dateDebut: PropertyStatus.Required,
      dateFin: PropertyStatus.Required,
    },
  },
  mission: {
    $model: obligatoireSurCIPI,
    posteOccupe: PropertyStatus.NotPresent,
    duree: PropertyStatus.Required,
    dateDebut: PropertyStatus.Required,
    dateFin: PropertyStatus.Required,
  },
  validationNotice: PropertyStatus.Required,
  attestationPiecesJustificatives: PropertyStatus.Required,
  // Communs
  lieuSignature: PropertyStatus.Required,
  acceptationCg: PropertyStatus.Required,
  financement: {
    $model: (_value, _parent, _model, context) => {
      if (context.typeContext === TypesContextesAutorisesEnum.Prestataire) return PropertyStatus.NotPresent
    },
    attestationResteChargeContribution: PropertyStatus.Required,
    modePriseEnChargeReste: (_value, parent) => {
      if (parent?.attestationResteChargeContribution === true) return PropertyStatus.Required
      return PropertyStatus.NotPresent
    },
  },
  dateSignature: PropertyStatus.Required,
  dateDepotDrf: PropertyStatus.NotPresent,
}
export const propertyStatusConfigurationReservation: PropertyStatusConfiguration<ContratCxpiDTO, ContextReglesCxpi> = {
  employeur: {
    contact: {
      email: PropertyStatus.Optional,
      telephone: PropertyStatus.NotPresent,
    },
    effectifTotalEntreprise: PropertyStatus.NotPresent,
  },
  salarie: {
    $model: PropertyStatus.NotPresent,
  },
  contrat: {
    referenceProjet: PropertyStatus.Optional,
    referenceInterneEntreprise: PropertyStatus.Optional,
    nombreContrats: PropertyStatus.Required,
    parcoursPTP: (_value, _parent, model) =>
      model.sousDispositif === 'CDPI' ? PropertyStatus.Required : PropertyStatus.NotPresent,
  },
  formation: {
    dateDebutPrevisionnelle: PropertyStatus.Required,
    diplomeOuTitreVise: PropertyStatus.Required,
    typeQualification: PropertyStatus.Required,
    conventionCollective: (_value, parent) =>
      ['QualificationCC', 'CertificationRS'].includes(parent.typeQualification!)
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    niveau: (_value, parent) =>
      ['QualificationCC', 'CertificationRS'].includes(parent.typeQualification!)
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    coefficient: (_value, parent) =>
      ['QualificationCC', 'CertificationRS'].includes(parent.typeQualification!)
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    nombreCCP: (_value, parent) =>
      ['CertificatCompetencePro'].includes(parent.typeQualification!)
        ? PropertyStatus.Required
        : PropertyStatus.NotPresent,
    typeReferentielFormation: PropertyStatus.Required,
    rncp: {
      $model: (_, parent) =>
        parent.typeReferentielFormation === 'Rncp' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    },
    formacode: {
      $model: (_, parent) =>
        parent.typeReferentielFormation === 'Formacode' ? PropertyStatus.Required : PropertyStatus.NotPresent,
    },
    nsf: {
      $model: (_, parent) =>
        isNullUndefinedOrEmptyString(parent.typeQualification) ? PropertyStatus.NotPresent : PropertyStatus.Required,
    },
    intitule: (_, parent) =>
      isNullUndefinedOrEmptyString(parent.typeQualification) ? PropertyStatus.NotPresent : PropertyStatus.Required,
    objectifGeneralFormation: PropertyStatus.NotPresent,
    dureeTotale: PropertyStatus.Required,
    dureeTotaleBilans: PropertyStatus.Required,
    dureeMinimumMission: obligatoireSurCDPI,
    modules: {
      formationRealiseeDansEntreprise: PropertyStatus.Required,
      etablissementPrestataire: {
        $model: PropertyStatus.Required,
        siret: {
          $model: PropertyStatus.Required,
        },
      },
      modalitePedagogique: PropertyStatus.Required,
      lieuFormationIdentiqueCfaPrincipal: PropertyStatus.NotPresent,
      lieuFormation: {
        $model: PropertyStatus.NotPresent,
        voie: PropertyStatus.NotPresent,
        commune: PropertyStatus.NotPresent,
        codePostal: PropertyStatus.NotPresent,
      },

      dureeFoad: PropertyStatus.NotPresent,
      duree: PropertyStatus.Required,
      horairesFormation: PropertyStatus.NotPresent,
      dateDebut: PropertyStatus.Optional,
      dateFin: PropertyStatus.Optional,
    },
  },
  financement: {
    $model: PropertyStatus.NotPresent,
  },
  mission: {
    $model: (_model, parent) => {
      if (parent.sousDispositif !== 'CIPI') return PropertyStatus.NotPresent
      //if (parent.typeDemande === 'ReservationFonds') return PropertyStatus.NotPresent
      return PropertyStatus.Required
    },
    posteOccupe: PropertyStatus.NotPresent,
    duree: (_value, _model, parent) => {
      if (parent.sousDispositif !== 'CIPI') return PropertyStatus.NotPresent
      //if (parent.typeDemande === 'ReservationFonds') return PropertyStatus.NotPresent
      return PropertyStatus.Required
    },
    dateDebut: PropertyStatus.NotPresent,
    dateFin: PropertyStatus.NotPresent,
  },
  validationNotice: PropertyStatus.Required,
  attestationPiecesJustificatives: PropertyStatus.Required,
  lieuSignature: PropertyStatus.Required,
  // Communs
  acceptationCg: PropertyStatus.Required,
  dateSignature: PropertyStatus.Required,
  dateDepotDrf: PropertyStatus.NotPresent,
}
